import dayjs from 'dayjs';
import avatar from 'images/avatar.png';
import company from 'images/company.png';

import { cdnEndpoint } from 'constants/endpoints';

import {
  APITenant,
  APIUser,
  APIUserRight,
  APIUserTenant,
  APIUserType,
} from 'types/api';

export const getAvatarUrl = (user: APIUser) => {
  if (!user.avatar) {
    return avatar;
  }
  return `${cdnEndpoint()}files/${user.id}/images/${user.avatar}`;
};

export const getTenantAvatarUrl = (
  tenant?: APITenant | undefined,
) => {
  if (!tenant || !tenant.avatar) {
    return company;
  }
  return `${cdnEndpoint()}files/${tenant.id}/images/${
    tenant.avatar
  }`;
};

export const convertUrlToFile = async (url: string): Promise<File | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Failed to fetch avatar image:', response.statusText);
      return null;
    }

    const blob = await response.blob();
    const fileName = url.split('/').pop() || 'profile-picture.png';
    const fileType = blob.type || 'image/png';
    return new File([blob], fileName, { type: fileType });
  } catch (error) {
    console.error('Error converting avatar URL to file:', error);
    return null;
  }
};

export const downloadExportFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

export const moneyFormatter = (money: string | number | null) => {
  const formattedMoney = parseFloat(money?.toString() ?? '0').toLocaleString(
    'en-US',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  );

  return `$${formattedMoney}`;
};

export const camelCaseHumanized = (str: string): string => {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

export const hasRights = (
  tenants: APIUserTenant[],
  userRights: APIUserRight,
  tenantId?: string,
) => {
  if (!tenants || tenants.length === 0) {
    return false;
  }

  const userTenants = tenants?.find(it => it.tenant.id == tenantId);

  const userType = userTenants?.userType;
  const rights = userTenants?.userRights.map(it => it.right) || [];

  return userType == APIUserType.Admin || rights.includes(userRights);
};

export const humanizePascalCase = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const mergeSavedColumnsWithExisting = (
  savedColumns: Array<any>,
  columns: Array<any>,
) => {
  return savedColumns.map((savedColumn: any) => {
    const existingColumn = columns.find(
      (column: any) => column.dataId === savedColumn.dataId,
    );
    return { ...existingColumn, ...savedColumn };
  });
};

export const extractFiltersFromSavedColumns = (savedColumns: any[]) =>
  savedColumns
    .filter(it => it.filter)
    .reduce((acc, it) => {
      acc[it.dataId] = it.filter;
      return acc;
    }, {});

export const convertToUtcIsoString = (
  dateTimeValue: string | null | undefined,
  timezone: string,
): string | null => {
  if (!dateTimeValue) return null;

  // Get the "wall clock" time from the picker (what the user sees)
  const pickerValue = dayjs(dateTimeValue);
  const dateStr = pickerValue.format('YYYY-MM-DD HH:mm:ss');

  return dayjs.tz(dateStr, timezone).utc().toISOString();
};

export const convertFromUtcToTimezone = (
  utcDateTimeValue: string | null | undefined,
  timezone: string,
): string | null => {
  if (!utcDateTimeValue) return null;

  // Convert UTC datetime to the specified timezone
  return dayjs.utc(utcDateTimeValue).tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
};
